import { ActionIcon, Group, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { NumericFormat } from 'react-number-format';
import EditCustomVariable from 'shared/action-component/EditCustomVariable';
import {
  type CustomVariable,
  useDeleteCustomVariable,
} from '~/modules/Settings/Variables/CustomVariables/api';

type Props = {
  item: CustomVariable;
};

export const CustomVariablesItem = ({ item }: Props) => {
  const { t } = useTranslation();
  const { deleteCustomVariable } = useDeleteCustomVariable();

  const handleEdit = () =>
    modals.open({
      size: 'lg',
      title: t(i18nKeys.FORM.CUSTOM_VARIABLES.TITLE.EDIT),
      children: <EditCustomVariable customVariable={item} onClose={() => modals.closeAll()} />,
    });

  const handleDelete = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.CONFIRMATION),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.AYS_TO_DELETE)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteCustomVariable({ id: item.id }),
    });

  const renderValue = () => {
    if (item.defaultValue == null || item.defaultValue.toString() === '') return 'N/A';
    switch (item.varType) {
      case 'boolean':
        return t(i18nKeys[item.defaultValue.toString() === 'true' ? 'YES' : 'NO']);
      case 'date':
        return item.defaultValue.format('L');
      default:
        switch (item.display) {
          case 'money':
            return (
              <NumericFormat
                value={item.defaultValue}
                displayType="text"
                suffix="€"
                decimalSeparator="."
                fixedDecimalScale
                decimalScale={2}
                thousandSeparator=" "
              />
            );
          case 'money_in_cents':
            return (
              <NumericFormat
                value={Number(item.defaultValue) / 100}
                displayType="text"
                suffix="€"
                decimalSeparator="."
                fixedDecimalScale
                decimalScale={2}
                thousandSeparator=" "
              />
            );
          case 'percentage':
            return (
              <NumericFormat
                value={item.defaultValue}
                suffix="%"
                decimalScale={2}
                displayType="text"
              />
            );
          default:
            return item.defaultValue;
        }
    }
  };

  return (
    <Table.Tr>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td>{item.columnName}</Table.Td>
      <Table.Td>{t(tKeysLoose.COLUMN_TYPE[item.varType.toUpperCase()])}</Table.Td>
      <Table.Td>{t(i18nKeys.COMMON[item.modelType === 'invoice' ? 'INVOICE' : 'CLIENT'])}</Table.Td>
      <Table.Td>{renderValue()}</Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon variant="subtle" aria-label={t(i18nKeys.EDIT)} onClick={handleEdit}>
            <IconPencil size={18} stroke={1.5} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            aria-label={t(i18nKeys.DELETE)}
            onClick={handleDelete}
          >
            <IconTrash size={18} stroke={1.5} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
